import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  // Card,
  // CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  // Tooltip,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
// Temporarily removed @mui/lab due to babel issues
// Will use custom timeline implementation
import {
  // Person as PersonIcon,
  Store as StoreIcon,
  DirectionsCar as CarIcon,
  DirectionsBus as BusIcon,
  DirectionsWalk as WalkIcon,
  AccessTime as TimeIcon,
  // Speed as SpeedIcon,
  Route as RouteIcon,
  // LocationOn as LocationIcon,
  Home as HomeIcon,
  // TripOrigin as StartIcon,
  Flag as EndIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Map as MapIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
// ;
import Map2GIS from './Map2GIS';
import axiosInstance from '../utils/axiosConfig';
import {format, parseISO} from "date-fns";

interface RouteDetail {
  route_id: string;
  worker_id: number;
  worker_name: string;
  worker_email: string;
  transport_type: string;
  date: string;
  start_time: string;
  end_time: string;
  total_distance: number; // meters
  total_duration: number; // minutes
  total_service_time: number; // minutes
  visits: VisitDetail[];
  route_polyline?: string;
}

interface VisitDetail {
  visit_id: string;
  sequence: number;
  store_id: number;
  store_name: string;
  store_address: string;
  lat: number;
  lng: number;
  arrival_time: string;
  departure_time: string;
  service_duration: number; // minutes
  distance_from_previous: number; // meters
  travel_time_from_previous: number; // minutes
  program_name?: string;
  visit_requirements?: string;
}

interface OptimizationRouteViewProps {
  planId: number;
  date?: string;
  workerId?: number;
  showMap?: boolean;
  showTimeline?: boolean;
}

const OptimizationRouteView: React.FC<OptimizationRouteViewProps> = ({
  planId,
  date,
  workerId,
  showMap = true,
  showTimeline = true
}) => {
  const [routes, setRoutes] = useState<RouteDetail[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<RouteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [expandedRoutes, setExpandedRoutes] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'timeline' | 'map'>('list');

  useEffect(() => {
    loadRoutes();
  }, [planId, date, workerId]);

  const loadRoutes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = { plan_id: planId };
      if (date) params.date = date;
      if (workerId) params.worker_id = workerId;
      
      const response = await axiosInstance.get('/api/v1/optimization/routes', { params });
      setRoutes(response.data);
      
      if (response.data.length > 0 && !selectedRoute) {
        setSelectedRoute(response.data[0]);
      }
    } catch (err: any) {
      setError('Failed to load routes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getTransportIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'auto':
      case 'car':
        return <CarIcon />;
      case 'public':
      case 'public_transport':
        return <BusIcon />;
      default:
        return <WalkIcon />;
    }
  };

  const toggleRouteExpansion = (routeId: string) => {
    const newExpanded = new Set(expandedRoutes);
    if (newExpanded.has(routeId)) {
      newExpanded.delete(routeId);
    } else {
      newExpanded.add(routeId);
    }
    setExpandedRoutes(newExpanded);
  };

  // Calculate route statistics
  const routeStats = useMemo(() => {
    if (!routes.length) return null;
    
    const totalDistance = routes.reduce((sum, r) => sum + r.total_distance, 0);
    const totalDuration = routes.reduce((sum, r) => sum + r.total_duration, 0);
    const totalVisits = routes.reduce((sum, r) => sum + r.visits.length, 0);
    const avgVisitsPerRoute = totalVisits / routes.length;
    
    return {
      totalRoutes: routes.length,
      totalDistance: totalDistance / 1000, // km
      totalDuration: totalDuration / 60, // hours
      totalVisits,
      avgVisitsPerRoute: avgVisitsPerRoute.toFixed(1)
    };
  }, [routes]);

  // Prepare map data
  const mapData = useMemo(() => {
    if (!selectedRoute) return { stores: [], routes: [] };
    
    const stores = selectedRoute.visits.map(visit => ({
      id: visit.store_id,
      name: visit.store_name,
      address: visit.store_address,
      lat: visit.lat,
      lng: visit.lng,
      sequence: visit.sequence
    }));
    
    const route = {
      id: selectedRoute.route_id,
      worker: selectedRoute.worker_name,
      path: selectedRoute.visits.map(v => ({ lat: v.lat, lng: v.lng }))
    };
    
    return { stores, routes: [route] };
  }, [selectedRoute]);

  return (
    <Box>
      {/* Statistics Summary */}
      {routeStats && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} justifyContent="space-around">
            <Stack alignItems="center">
              <Typography variant="caption" color="textSecondary">Total Routes</Typography>
              <Typography variant="h6">{routeStats.totalRoutes}</Typography>
            </Stack>
            <Stack alignItems="center">
              <Typography variant="caption" color="textSecondary">Total Distance</Typography>
              <Typography variant="h6">{routeStats.totalDistance.toFixed(1)} km</Typography>
            </Stack>
            <Stack alignItems="center">
              <Typography variant="caption" color="textSecondary">Total Duration</Typography>
              <Typography variant="h6">{routeStats.totalDuration.toFixed(1)} hrs</Typography>
            </Stack>
            <Stack alignItems="center">
              <Typography variant="caption" color="textSecondary">Total Visits</Typography>
              <Typography variant="h6">{routeStats.totalVisits}</Typography>
            </Stack>
            <Stack alignItems="center">
              <Typography variant="caption" color="textSecondary">Avg Visits/Route</Typography>
              <Typography variant="h6">{routeStats.avgVisitsPerRoute}</Typography>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* View Mode Selector */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant={viewMode === 'list' ? 'contained' : 'outlined'}
          onClick={() => setViewMode('list')}
          startIcon={<AssignmentIcon />}
        >
          List View
        </Button>
        {showTimeline && (
          <Button
            variant={viewMode === 'timeline' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('timeline')}
            startIcon={<TimelineIcon />}
          >
            Timeline
          </Button>
        )}
        {showMap && (
          <Button
            variant={viewMode === 'map' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('map')}
            startIcon={<MapIcon />}
          >
            Map View
          </Button>
        )}
      </Stack>

      {loading && <LinearProgress />}
      {/*{error && <Alert severity="error">{error}</Alert>}*/}

      {/* Main Content */}
      <Stack direction="row" spacing={3}>
        {/* Routes List */}
        <Box sx={{ flex: viewMode === 'map' ? '0 0 33%' : '1', minWidth: 0 }}>
          <Paper sx={{ p: 2, maxHeight: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Routes
            </Typography>
            <List>
              {routes.map((route) => (
                <React.Fragment key={route.route_id}>
                  <ListItem
                    onClick={() => setSelectedRoute(route)}
                    sx={{ 
                      cursor: 'pointer',
                      bgcolor: selectedRoute?.route_id === route.route_id ? 'action.selected' : 'transparent'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        {getTransportIcon(route.transport_type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography>{route.worker_name}</Typography>
                          <Chip
                            size="small"
                            label={`${route.visits.length} visits`}
                            color="primary"
                          />
                        </Stack>
                      }
                      secondary={
                        <Stack spacing={0.5}>
                          <Typography variant="caption">
                            {format(parseISO(route.start_time), 'HH:mm')} - {format(parseISO(route.end_time), 'HH:mm')}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              icon={<RouteIcon />}
                              label={`${(route.total_distance / 1000).toFixed(1)} km`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={<TimeIcon />}
                              label={`${route.total_duration} min`}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </Stack>
                      }
                    />
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRouteExpansion(route.route_id);
                      }}
                    >
                      {expandedRoutes.has(route.route_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </ListItem>
                  
                  <Collapse in={expandedRoutes.has(route.route_id)}>
                    <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                      <List dense>
                        {route.visits.map((visit, _index) => (
                          <ListItem key={visit.visit_id}>
                            <ListItemIcon>
                              <Avatar 
                                sx={{ 
                                  width: 30, 
                                  height: 30,
                                  bgcolor: _index === 0 ? 'primary.main' : _index === route.visits.length - 1 ? 'error.main' : 'secondary.main'
                                }}
                              >
                                {_index === 0 ? <HomeIcon fontSize="small" /> : _index === route.visits.length - 1 ? <EndIcon fontSize="small" /> : <StoreIcon fontSize="small" />}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    {format(parseISO(visit.arrival_time), 'HH:mm')}
                                  </Typography>
                                  <Typography variant="subtitle2">
                                    {visit.store_name}
                                  </Typography>
                                </Stack>
                              }
                              secondary={
                                <Stack spacing={0.5}>
                                  <Typography variant="caption" color="textSecondary">
                                    {visit.store_address}
                                  </Typography>
                                  <Stack direction="row" spacing={1}>
                                    <Chip
                                      label={`${visit.service_duration} min`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    {_index > 0 && (
                                      <Chip
                                        label={`${(visit.distance_from_previous / 1000).toFixed(1)} km`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    )}
                                  </Stack>
                                </Stack>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Collapse>
                  
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>

        {/* Map View */}
        {viewMode === 'map' && showMap && selectedRoute && (
          <Box sx={{ flex: '1', minWidth: 0 }}>
            <Paper sx={{ p: 2, height: '70vh' }}>
              <Typography variant="h6" gutterBottom>
                Route Map - {selectedRoute.worker_name}
              </Typography>
              <Box sx={{ height: 'calc(100% - 40px)' }}>
                <Map2GIS
                  stores={mapData.stores}
                  lat={mapData.stores.length > 0 ? mapData.stores[0].lat : undefined}
                  lng={mapData.stores.length > 0 ? mapData.stores[0].lng : undefined}
                  height="100%"
                />
              </Box>
            </Paper>
          </Box>
        )}
      </Stack>

      {/* Timeline View */}
      {viewMode === 'timeline' && showTimeline && selectedRoute && (
        <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Route Timeline - {selectedRoute.worker_name}
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Store</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Service Time</TableCell>
                      <TableCell>Distance</TableCell>
                      <TableCell>Travel Time</TableCell>
                      <TableCell>Program</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedRoute.visits.map((visit, _index) => (
                      <TableRow key={visit.visit_id}>
                        <TableCell>{visit.sequence}</TableCell>
                        <TableCell>
                          {format(parseISO(visit.arrival_time), 'HH:mm')} - {format(parseISO(visit.departure_time), 'HH:mm')}
                        </TableCell>
                        <TableCell>{visit.store_name}</TableCell>
                        <TableCell>{visit.store_address}</TableCell>
                        <TableCell>{visit.service_duration} min</TableCell>
                        <TableCell>
                          {_index > 0 ? `${(visit.distance_from_previous / 1000).toFixed(1)} km` : '-'}
                        </TableCell>
                        <TableCell>
                          {_index > 0 ? `${visit.travel_time_from_previous} min` : '-'}
                        </TableCell>
                        <TableCell>{visit.program_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
        </Box>
      )}
    </Box>
  );
};

export default OptimizationRouteView;